import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import {
  importListsApi,
  type CreateImportListRequest,
  type ImportList,
  type ImportListKind,
} from "@/api/importLists";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Checkbox, Field, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

/**
 * Movies → Import lists. Closes the Radarr-onboarding gap (issue #51):
 * point at a Trakt / IMDB / TMDB list and auto-add monitored movies on
 * a recurring schedule.
 *
 * The CRUD shape mirrors the Saved Searches page (modal-driven create /
 * edit, table list, per-row sync-now / enable / monitored toggles) so
 * users have one mental model for "scheduled background fan-out".
 */

type EditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; row: ImportList };

const KIND_LABEL: Record<ImportListKind, string> = {
  "trakt-list": "Trakt",
  "imdb-list": "IMDB",
  "tmdb-list": "TMDB",
};

const KIND_HINT: Record<ImportListKind, string> = {
  "trakt-list": "user/slug — e.g. justin/imdb-top-250",
  "imdb-list": "ls012345678 — the id from your list URL",
  "tmdb-list": "numeric list id from TMDB",
};

export function MovieImportLists() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<EditingState>({ mode: "closed" });
  const [syncingId, setSyncingId] = useState<number | null>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["import-lists"],
    queryFn: importListsApi.list,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => importListsApi.remove(id),
    onSuccess: () => {
      toast("Import list removed", "success");
      qc.invalidateQueries({ queryKey: ["import-lists"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const toggle = async (
    row: ImportList,
    field: "enabled" | "monitored",
  ) => {
    try {
      await importListsApi.update(row.id, { [field]: !row[field] });
      qc.invalidateQueries({ queryKey: ["import-lists"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  const syncNow = async (row: ImportList) => {
    setSyncingId(row.id);
    try {
      const outcome = await importListsApi.syncNow(row.id);
      const summary = `seen ${outcome.total_seen}, added ${outcome.added}` +
        (outcome.unresolved
          ? `, ${outcome.unresolved} unresolved`
          : "") +
        (outcome.failed ? `, ${outcome.failed} failed` : "");
      toast(`"${row.name}" synced — ${summary}`, outcome.added > 0 ? "success" : "info");
      qc.invalidateQueries({ queryKey: ["import-lists"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Sync failed", "error");
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Import Lists"
        description="Sync your library against Trakt watchlists, IMDB lists, or TMDB lists. Movies on the upstream list are auto-added and monitored — fresh additions are picked up on every sync."
        actions={
          <Button onClick={() => setEditing({ mode: "create" })}>
            New import list
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          title="No import lists"
          description="Add a Trakt watchlist, IMDB list, or TMDB list to bulk-populate your library on a recurring schedule."
          action={
            <Button onClick={() => setEditing({ mode: "create" })}>
              New import list
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Kind</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 font-medium">Last sync</th>
                <th className="px-4 py-2 font-medium">Hits</th>
                <th className="px-4 py-2 font-medium">Enabled</th>
                <th className="px-4 py-2 font-medium">Monitored</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-4 py-2 font-medium">{row.name}</td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {KIND_LABEL[row.kind]}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-foreground/70">
                    {row.source_url_or_id}
                  </td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {row.last_synced_at
                      ? new Date(row.last_synced_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {row.hit_count}
                  </td>
                  <td className="px-4 py-2">
                    <Checkbox
                      checked={row.enabled}
                      onChange={() => toggle(row, "enabled")}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Checkbox
                      checked={row.monitored}
                      onChange={() => toggle(row, "monitored")}
                    />
                  </td>
                  <td className="space-x-2 px-4 py-2 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={syncingId === row.id}
                      onClick={() => syncNow(row)}
                    >
                      Sync now
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditing({ mode: "edit", row })}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Remove import list "${row.name}"?`)) {
                          removeMut.mutate(row.id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ImportListModal
        editing={editing}
        onClose={() => setEditing({ mode: "closed" })}
      />
    </div>
  );
}

interface FormState {
  name: string;
  kind: ImportListKind;
  source_url_or_id: string;
  monitored: boolean;
  enabled: boolean;
}

const blankForm = (): FormState => ({
  name: "",
  kind: "trakt-list",
  source_url_or_id: "",
  monitored: true,
  enabled: true,
});

const formFromRow = (row: ImportList): FormState => ({
  name: row.name,
  kind: row.kind,
  source_url_or_id: row.source_url_or_id,
  monitored: row.monitored,
  enabled: row.enabled,
});

const toBody = (f: FormState): CreateImportListRequest => ({
  name: f.name.trim(),
  kind: f.kind,
  source_url_or_id: f.source_url_or_id.trim(),
  monitored: f.monitored,
  enabled: f.enabled,
});

function ImportListModal({
  editing,
  onClose,
}: {
  editing: EditingState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(blankForm());
  const [submitting, setSubmitting] = useState(false);

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";
  const editingRow = editing.mode === "edit" ? editing.row : null;

  useEffect(() => {
    if (editing.mode === "create") setForm(blankForm());
    if (editing.mode === "edit") setForm(formFromRow(editing.row));
  }, [editing]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = toBody(form);
      if (!body.name) {
        toast("Name is required", "error");
        return;
      }
      if (!body.source_url_or_id) {
        toast("Source is required", "error");
        return;
      }
      if (isEdit && editingRow) {
        await importListsApi.update(editingRow.id, body);
        toast("Import list updated", "success");
      } else {
        await importListsApi.create(body);
        toast("Import list created", "success");
      }
      qc.invalidateQueries({ queryKey: ["import-lists"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isEdit
          ? `Edit ${editingRow?.name ?? "import list"}`
          : "New import list"
      }
      width="md"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <TextInput
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. IMDB Top 250"
          />
        </Field>

        <Field label="Provider">
          <Select
            value={form.kind}
            onChange={(e) =>
              setForm((f) => ({ ...f, kind: e.target.value as ImportListKind }))
            }
          >
            <option value="trakt-list">Trakt — public list</option>
            <option value="imdb-list">IMDB — public list</option>
            <option value="tmdb-list">TMDB — public list</option>
          </Select>
        </Field>

        <Field label="Source" hint={KIND_HINT[form.kind]}>
          <TextInput
            required
            value={form.source_url_or_id}
            onChange={(e) =>
              setForm((f) => ({ ...f, source_url_or_id: e.target.value }))
            }
            placeholder={KIND_HINT[form.kind]}
          />
        </Field>

        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.enabled}
              onChange={(e) =>
                setForm((f) => ({ ...f, enabled: e.target.checked }))
              }
            />
            <span>Enabled (auto-sync hourly)</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.monitored}
              onChange={(e) =>
                setForm((f) => ({ ...f, monitored: e.target.checked }))
              }
            />
            <span>Add as monitored</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-line/60 pt-4">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
